import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { Home, NotebookPen, ShieldCheck, User, Plus, HelpCircle, Users } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useFinance } from "@/context/FinanceContext";
import { GuideSheet } from "@/components/GuideSheet";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const TABS = [
  { to: "/app/beranda", label: "Beranda", icon: Home, testId: "nav-beranda" },
  { to: "/app/catatan", label: "Catatan", icon: NotebookPen, testId: "nav-catatan" },
  { to: "/app/ketahanan", label: "Ketahanan", icon: ShieldCheck, testId: "nav-ketahanan" },
  { to: "/app/komunitas", label: "Komunitas", icon: Users, testId: "nav-komunitas" },
  { to: "/app/profil", label: "Profil", icon: User, testId: "nav-profil" },
];

export function AppLayout() {
  const { report } = useApp();
  const { hasHousehold, init } = useFinance();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [guideOpen, setGuideOpen] = useState(false);
  const [notif, setNotif] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    let stop = false;
    const fetchNotif = () =>
      api.communityNotifications().then((r) => { if (!stop) setNotif(r.count || 0); }).catch(() => {});
    fetchNotif();
    const t = setInterval(fetchNotif, 20000);
    const onSeen = () => setNotif(0);
    window.addEventListener("community-seen", onSeen);
    return () => { stop = true; clearInterval(t); window.removeEventListener("community-seen", onSeen); };
  }, [isAuthenticated]);

  // Authenticated users can track finances even without an assessment:
  // auto-initialize a household on first entry.
  useEffect(() => {
    if (!report && !hasHousehold && isAuthenticated) init();
  }, [report, hasHousehold, isAuthenticated, init]);

  // Wait for the session check before deciding — avoids bouncing an authenticated
  // deep link / refresh to the landing page while /session is still loading.
  if (authLoading) return null;

  // Anonymous with neither a report nor a household -> back to landing.
  if (!report && !hasHousehold && !isAuthenticated) return <Navigate to="/" replace />;
  if (!report && !hasHousehold) return null; // initializing household

  return (
    <div className="min-h-screen w-full bg-[#f8f8f7]">
      {/* Desktop sidebar (lg+) — mobile keeps the bottom nav below */}
      <aside
        data-testid="desktop-sidebar"
        className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-zinc-200 lg:bg-white lg:px-4 lg:py-6"
      >
        <div className="flex items-center gap-2.5 px-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600">
            <ShieldCheck className="h-5 w-5 text-white" />
          </span>
          <div>
            <p className="font-heading text-sm font-extrabold text-zinc-900">CeKKeU</p>
            <p className="text-[10px] text-zinc-400">Kesehatan Finansial Keluarga</p>
          </div>
        </div>
        <button
          data-testid="sidenav-catat"
          onClick={() => navigate("/app/catat")}
          className="mt-6 flex h-11 items-center justify-center gap-2 rounded-full bg-emerald-600 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} /> Catat Transaksi
        </button>
        <nav className="mt-6 flex flex-1 flex-col gap-1">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`sidenav-${t.testId.replace("nav-", "")}`}
              className={({ isActive }) =>
                cn(
                  "relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive ? "bg-emerald-50 text-emerald-700" : "text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800"
                )
              }
            >
              <t.icon className="h-5 w-5" />
              <span className="font-heading">{t.label}</span>
              {t.to === "/app/komunitas" && notif > 0 && (
                <span
                  data-testid="sidenav-komunitas-badge"
                  className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white"
                >
                  {notif > 9 ? "9+" : notif}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <button
          data-testid="open-guide-desktop"
          onClick={() => setGuideOpen(true)}
          className="flex items-center gap-2 rounded-xl border border-zinc-200 px-3 py-2.5 text-xs font-bold text-zinc-600 transition-colors hover:bg-zinc-50"
        >
          <HelpCircle className="h-4 w-4 text-emerald-600" /> Panduan
        </button>
      </aside>

      {/* Main column: phone-width on mobile, fluid on desktop */}
      <div className="mx-auto min-h-screen w-full max-w-md pb-24 lg:mx-0 lg:max-w-none lg:pb-10 lg:pl-64">
        <div className="no-scrollbar mx-auto w-full lg:max-w-6xl lg:px-10 lg:pt-6">
          <Outlet />
        </div>

        {/* Global help (mobile) — overlay only, does not affect page layout */}
        <div className="pointer-events-none fixed bottom-0 left-1/2 z-40 w-full max-w-md -translate-x-1/2 lg:hidden">
          <button
            data-testid="open-guide"
            onClick={() => setGuideOpen(true)}
            className="pointer-events-auto absolute bottom-[84px] right-4 flex h-10 items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3.5 text-xs font-bold text-zinc-700 shadow-lg shadow-zinc-900/10 transition-colors hover:bg-zinc-50"
            aria-label="Buka panduan"
          >
            <HelpCircle className="h-4 w-4 text-emerald-600" /> Panduan
          </button>
        </div>
      </div>
      <GuideSheet open={guideOpen} onClose={() => setGuideOpen(false)} />

      <nav
        className="fixed bottom-0 left-1/2 z-50 flex h-[68px] w-full max-w-md -translate-x-1/2 items-center justify-around border-t border-zinc-200/80 bg-white/85 px-2 backdrop-blur-xl lg:hidden"
        data-testid="bottom-nav"
      >
        {TABS.slice(0, 2).map((t) => (
          <NavItem key={t.to} {...t} />
        ))}

        {/* Center quick-entry action */}
        <button
          data-testid="nav-catat"
          onClick={() => navigate("/app/catat")}
          className="flex flex-1 flex-col items-center gap-1"
          aria-label="Catat transaksi"
        >
          <span className="flex h-11 w-11 -translate-y-2 items-center justify-center rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-600/30">
            <Plus className="h-6 w-6" strokeWidth={2.5} />
          </span>
          <span className="-mt-1 font-heading text-[11px] font-medium text-emerald-700">Catat</span>
        </button>

        {TABS.slice(2).map((t) => (
          <NavItem key={t.to} {...t} badge={t.to === "/app/komunitas" ? notif : 0} />
        ))}
      </nav>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testId, badge = 0 }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "relative flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-[11px] font-medium transition-colors",
          isActive ? "text-emerald-600" : "text-zinc-500"
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className="relative">
            <Icon className="h-5 w-5" strokeWidth={isActive ? 2.4 : 1.8} />
            {badge > 0 && (
              <span
                data-testid={`${testId}-badge`}
                className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white"
              >
                {badge > 9 ? "9+" : badge}
              </span>
            )}
          </span>
          <span className="font-heading">{label}</span>
        </>
      )}
    </NavLink>
  );
}
